import { _ } from "libs";

import React, { useEffect, useState } from "react";
import withRedux from "../../withRedux";
import Modal from "../modal";
import { closeLeaderboardPopup, getLeaderboard, openPlayerPopup, showComparisonLeaderboardModal } from "../../actions";
import { RootState } from "../../reducers";
import { Championship, LeaderboardData, LeaderboardQueryParams, Player, User } from "../../types";
import { Row } from "./leaderboard";
import { InvisibleInfiniteLoader } from "../infiniteLoader";
import { Field, Form } from "react-final-form";
import leaderboardModal from "../../reducers/leaderboardModal";

function LeaderboardModal({
	show = false,
	championship = null,
	filterParams = null,
	modalTitle = null,
	emptyScreenInfo = null,
	emptyScreenThreshold = null,
	contextParams = null,
	onPlayerSelected = null,
	showSearchPlayer = false,
	showComparisonLeaderboardModal,
}: {
	show: boolean;
	championship: Championship;
	filterParams: LeaderboardQueryParams;
	modalTitle?: string;
	emptyScreenInfo?: string;
	emptyScreenThreshold?: number;
	contextParams?: any;
	onPlayerSelected?: any;
	showSearchPlayer?: boolean;
	showComparisonLeaderboardModal;
}) {
	const [leaderboards, setLeaderboards] = useState([] as LeaderboardData[]);
	const [loading, setLoading] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [selectedUser, selectUser] = useState(null as User);
	const [currentPage, setCurrentPage] = useState(0 as number);
	const [playerName, setPlayerName] = useState(null as string);

	const PAGE_SIZE = 20;

	useEffect(() => {
		if (!championship || !filterParams) {
			return;
		}
		setLoading(true);
		getLeaderboard(championship.uuid, currentPage, PAGE_SIZE, { ...filterParams, playerName }).then((res) => {
			if (!res) {
				setLoading(false);
				return;
			}
			const { data, pageRows, pageCurrent } = res;
			if (data.length < PAGE_SIZE) {
				setHasMore(false);
			} else {
				setHasMore(true);
			}
			setLeaderboards([...leaderboards, ...data]);
			setLoading(false);
			return () => {
				setLeaderboards([]);
			};
		});
	}, [championship, currentPage, filterParams, playerName]);

	if (show === false) {
		return null;
	}

	const close = () => {
		closeLeaderboardPopup();
		setHasMore(true);
		setLeaderboards([]);
		setPlayerName(null);
	};

	let searchTermTimer = null;

	const updateSearchTerm = (value) => {
		if (value?.length < 3 && value?.length > 0) {
			return;
		}

		if (searchTermTimer) {
			clearTimeout(searchTermTimer);
		}

		searchTermTimer = setTimeout(() => {
			setLeaderboards([]);
			setPlayerName(value);
		}, 1500);
	};

	const onViewChangeDown = async (inView: boolean, observerEntry: IntersectionObserverEntry) => {
		if (!championship) {
			return;
		}
		const shouldLoad = inView && leaderboards.length > 0 && hasMore;

		if (shouldLoad) {
			setCurrentPage(currentPage + 1);
		}
	};

	const getOnPlayerSelected = (player: Player, championship?: Championship) => {
		if (onPlayerSelected) {
			return () => {
				setLoading(true);
				onPlayerSelected(player, championship).then(
					(res) => {
						close();
						contextParams?.backAction?.();
					},
					(err) => {
						close();
						contextParams?.backAction?.();
					}
				);
			};
		}
		return () => {
			close();
			openPlayerPopup(
				player.playerId,
				true,
				{ championshipName: championship.name },
				{
					championship,
					leagueId: player.leagueId,
					backAction: () => {
						showComparisonLeaderboardModal(
							filterParams,
							false,
							championship,
							onPlayerSelected,
							modalTitle,
							contextParams?.backAction,
							contextParams?.addAction,
							contextParams?.removeAction
						);
					},
				}
			);
		};
	};

	const removePlayer = (playerId: string, index: number) => {
		let p;

		if (contextParams?.removeAction) {
			setLoading(true);
			p = contextParams.removeAction?.(playerId);
		} else {
			p = Promise.resolve();
		}

		p.then(
			(res) => {
				let l = leaderboards;
				l.splice(index, 1);
				setLeaderboards(l);
				setLoading(false);
			},
			(rej) => setLoading(false)
		);
	};

	return (
		<Modal
			title={modalTitle}
			largeTitle={true}
			fullHeight={true}
			onCloseClicked={close}
			onBackClicked={
				contextParams?.backAction
					? () => {
							close();
							contextParams.backAction?.();
					  }
					: null
			}
			onAddClicked={
				contextParams?.addAction
					? () => {
							close();
							contextParams.addAction?.();
					  }
					: null
			}
			onInfoClicked={
				contextParams?.infoAction
					? () => {
							close();
							contextParams.infoAction?.();
					  }
					: null
			}
		>
			{showSearchPlayer && (
				<Form
					onSubmit={() => {}}
					render={({ handleSubmit, form, submitting, pristine, values }) => (
						<form onSubmit={handleSubmit} className="searchBar mb-4">
							<div className="field searchFieldWithIcon">
								<Field name="term">
									{({ input, meta }) => (
										<div className="input">
											<input
												{...input}
												onChange={(event) => {
													input.onChange(event);
													updateSearchTerm(event.target.value);
												}}
												type="text"
												placeholder={_("form.searchPlayer.placeholder")}
												disabled={loading}
											/>
										</div>
									)}
								</Field>
							</div>
						</form>
					)}
				/>
			)}
			<div className="table players -mx-3">
				{leaderboards.map((row: LeaderboardData, key: number) => {
					return (
						<Row
							key={`${row.player.userId}_${key}_${row.rank.rank}`}
							player={row.player}
							rank={row.rank}
							position={row.rank.rank}
							championship={championship}
							index={key}
							onClick={() => {
								getOnPlayerSelected(row.player, championship)();
							}}
							selected={selectedUser ? row.player.userId === selectedUser.userId : false}
							onRemove={
								contextParams?.removeAction ? () => {
									removePlayer(row.player.userId, key);
								} : null
							}
						/>
					);
				})}
				{!loading &&
					emptyScreenInfo &&
					emptyScreenThreshold !== null &&
					leaderboards.length <= emptyScreenThreshold && (
						<div className="emptyScreen">
							<p className="hint">{emptyScreenInfo}</p>
						</div>
					)}
				{hasMore ? <InvisibleInfiniteLoader onViewChange={onViewChangeDown} loading={loading} /> : null}
			</div>
		</Modal>
	);
}

function mapStateToProps({
	leaderboardModal: {
		show,
		championship,
		filterParams,
		modalTitle,
		emptyScreenInfo,
		emptyScreenThreshold,
		contextParams,
		showSearchPlayer,
		onPlayerSelected,
	},
}: RootState) {
	return {
		show,
		championship,
		filterParams,
		modalTitle,
		emptyScreenInfo,
		emptyScreenThreshold,
		contextParams,
		showSearchPlayer,
		onPlayerSelected,
	};
}

export default withRedux({ showComparisonLeaderboardModal }, mapStateToProps)(LeaderboardModal);
